import React, { memo, useCallback, useEffect, useState } from "react";
import { DialogTitle, DialogContent, Button, Box } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { motion, AnimatePresence } from "framer-motion";
import Separator from "../../core/components/Separator.tsx";
import SelectEmbarqueV2 from "../SelectEmbarqueV2/SelectEmbarqueV2.js";
import { buscaRegras } from "../../services/ModuloGerenciador/Usuario.js";
import { useProfile } from "../../contexts/ProfileContext.tsx";

interface Regra {
	regrasId: number;
	regra: string;
	check: boolean;
}

interface Regras {
	[key: string]: Regra[];
}

interface ModalPermissaoUsuarioProps {
	open: boolean;
	onClose: () => void;
	regras: Regras;
	values: { regras: number[] };
	setValues: React.Dispatch<React.SetStateAction<{ regras: number[] }>>;
	aceitarSolicitacao: () => void;
	listaUsuarios: any;
}

const modalVariants = {
	hidden: { opacity: 0 },
	visible: { opacity: 1 },
	exit: { opacity: 0 },
};

function ModalPermissaoUsuario({
	open,
	onClose,
	regras,
	values,
	setValues,
	aceitarSolicitacao,
	listaUsuarios
}: ModalPermissaoUsuarioProps) {
	const [usuarioId, setUsuarioId] = useState();
	const [valuesSelect, setValuesSelect] = useState<any>([]);
	const [allSelected, setAllSelected] = useState(false);
	const { profile } = useProfile();
	useEffect(() => {
		if (usuarioId !== undefined) {
			buscaRegras(usuarioId, profile?.token).then((res) => {
				const regras: any = [];
				res.data.forEach((element: any) => {
					regras.push(element.regras.regra);
				});

				formatAndSetRegras(regras);
			});
		}
	}, [usuarioId]);

	const formatAndSetRegras = useCallback(
		(userRegras) => {
			const regrasIdSelecionados: number[] = [];

			for (const categoria in regras) {
				const lista = regras[categoria];
				for (const item of lista) {
					if (userRegras.includes(item.regra)) {
						regrasIdSelecionados.push(item.regrasId);
					}
				}
			}

			setValues((current) => ({
				...current,
				regras: regrasIdSelecionados,
			}));
		},
		[regras]
	);

	useEffect(() => {
		const allChecked = Object.values(regras)
			.flat()
			.every((regra) => values.regras.includes(regra.regrasId));
		setAllSelected(allChecked);
	}, [regras, values.regras]);

	const handleCheckboxChange = (
		dado: Regra,
		e: React.ChangeEvent<HTMLInputElement>
	) => {
		if (e.target.checked) {
			setValues((current) => ({
				...current,
				regras: [...values.regras, dado.regrasId],
			}));
			return;
		}
		const arr = values.regras.filter((item) => item !== dado.regrasId);
		setValues((current) => ({
			...current,
			regras: arr,
		}));
	};

	const handleSelectAllChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setAllSelected(e.target.checked);
		if (e.target.checked) {
			const allRegrasIds = Object.values(regras)
				.flat()
				.map((regra) => regra.regrasId);
			setValues((current) => ({
				...current,
				regras: allRegrasIds,
			}));
			return;
		}

		setValues((current) => ({
			...current,
			regras: [],
		}));
	};

	return (
		<AnimatePresence>
			{open && (
				<motion.div
					onClick={onClose}
					style={{
						position: "fixed",
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						backgroundColor: "#000C18B3",
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						margin: 0,
						zIndex: 1000,
						pointerEvents: open ? "auto" : "none",
					}}
					className="transition"
					initial="hidden"
					animate="visible"
					exit="exit"
					variants={modalVariants}
					transition={{ duration: 0.3 }}
				>
					<motion.div
						onClick={(e) => e.stopPropagation()}
						style={{
							width: "100%",
							maxWidth: "90%",
							maxHeight: "90%",
							backgroundColor: "white",
							overflow: "hidden",
							position: "relative",
							display: "flex",
							flexDirection: "column",
						}}
						initial={{ opacity: 0, y: 50 }}
						exit={{ opacity: 0, y: 50 }}
						animate={{
							opacity: 1,
							y: 0,
						}}
						transition={{ duration: 0.3 }}
					>
						<DialogTitle
							sx={{
								display: "flex",
								flexDirection: "row",
								justifyContent: "space-between",
								alignItems: "center",
								width: "100%",
								padding: "12px",
							}}
						>
							<div style={{ width: "24px", height: "24px" }} />
							<div>Defina as Permissões do Usuário</div>
							<CloseIcon sx={{ cursor: "pointer" }} onClick={onClose} />
						</DialogTitle>
						<DialogContent>
							<div>
								<p style={{ textAlign: 'center' }} className="input-label-emb">Clonar permissões do usuário:</p>
								<SelectEmbarqueV2
									classeCss={250}
									onChange={(e) => {
										setUsuarioId(e.id);
										setValuesSelect((current) => ({
											...current,
											usuarioIdRegra: e,
										}));
									}}
									value={valuesSelect.usuarioIdRegra}
									id="idUsuario"
									titulo="Empresa"
									dadosSelect={listaUsuarios}
									referenciaId="usuario"
									referenciaItem="usuario"
									info={false}
								/>
							</div>
							<div
								style={{
									display: "flex",
									flexDirection: "column",
									width: "100%",
									alignItems: "center",
								}}
							>
								<Separator size="md" />
								<Box
									display="flex"
									flexDirection="row"
									gap="5px"
									alignItems="center"
								>
									<input
										type="checkbox"
										checked={allSelected}
										onChange={handleSelectAllChange}
									/>
									<strong>Selecionar/Desselecionar Tudo</strong>
								</Box>
								<Separator size="2xl" />
								<div className="containerRegras">
									{Object.keys(regras).map((key) => (
										<div className="usuario-regras-colunm" key={key}>
											<h4>{key}</h4>
											<Separator size="md" />
											<div className="itemRegras">
												{regras[key].map((dado) => (
													<Box
														display="flex"
														flexDirection="row"
														key={dado.regra}
														gap="5px"
													>
														<input
															type="checkbox"
															checked={values.regras.includes(dado.regrasId)}
															onChange={(e) => handleCheckboxChange(dado, e)}
														/>
														{dado.regra}{" "}
														{dado.check ? <p> - Já possui</p> : <p></p>}
													</Box>
												))}
											</div>
											<Separator size="md" />
										</div>
									))}
								</div>
								<div className="botaoConcluir">
									<Button
										sx={{ width: 180 }}
										variant="contained"
										onClick={aceitarSolicitacao}
									>
										Aceitar
									</Button>
								</div>
							</div>
						</DialogContent>
					</motion.div>
				</motion.div>
			)}
		</AnimatePresence>
	);
}

export default memo(ModalPermissaoUsuario);
